import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface UseDialogSubmitStateOptions {
  /** Whether the mutation/action is currently running */
  isSubmitting: boolean;
  /** For edit dialogs: whether form values differ from initial values */
  isDirty?: boolean;
  /** Validation error message (empty string = valid) */
  validationError?: string;
  /** For create dialogs: whether required fields are filled */
  isRequiredFilled?: boolean;
  /** For confirm dialogs: whether user has confirmed (typed DELETE, checked box, etc.) */
  isConfirmed?: boolean;
  /**
   * A refusal the dialog knows about and this hook cannot work out: a plan that does not allow
   * the action, a permission, an entitlement. Non-empty disables the button and becomes the
   * tooltip. It outranks every check below, because none of them is the reason — a user told
   * "No changes to save" on a Free plan will make a change and be refused again.
   */
  blockedReason?: string;
}

interface DialogSubmitState {
  /** Whether the submit button should be disabled */
  disabled: boolean;
  /** Tooltip to show when disabled, explaining why */
  disabledTooltip: string;
}

/**
 * Standardizes save/confirm button disable logic across all dialogs.
 *
 * Each option is opt-in (undefined = not checked). The hook computes
 * `disabled` and `disabledTooltip` based on priority:
 * isSubmitting → blockedReason → validationError → isDirty → isRequiredFilled → isConfirmed
 *
 * Usage:
 * - Edit dialogs: pass `isDirty` + `validationError` + `isSubmitting`
 * - Create dialogs: pass `isRequiredFilled` + `isSubmitting`
 * - Confirm dialogs: pass `isConfirmed` + `isSubmitting`
 */
export function useDialogSubmitState(options: UseDialogSubmitStateOptions): DialogSubmitState {
  const { t } = useTranslation();
  const { isSubmitting, isDirty, validationError, isRequiredFilled, isConfirmed, blockedReason } =
    options;

  return useMemo(() => {
    // No tooltip while submitting — the spinner conveys loading
    if (isSubmitting) {
      return { disabled: true, disabledTooltip: '' };
    }

    if (blockedReason) {
      return { disabled: true, disabledTooltip: blockedReason };
    }

    if (validationError) {
      return { disabled: true, disabledTooltip: validationError };
    }

    if (isDirty !== undefined && !isDirty) {
      return { disabled: true, disabledTooltip: t('No changes to save') };
    }

    if (isRequiredFilled !== undefined && !isRequiredFilled) {
      return {
        disabled: true,
        disabledTooltip: t('Please fill in required fields'),
      };
    }

    if (isConfirmed !== undefined && !isConfirmed) {
      return {
        disabled: true,
        disabledTooltip: t('Please confirm to continue'),
      };
    }

    return { disabled: false, disabledTooltip: '' };
  }, [isSubmitting, isDirty, validationError, isRequiredFilled, isConfirmed, blockedReason, t]);
}
